import type { PermissionKey } from '@nexa/auth';
import type { TFunction } from 'i18next';
import { vendorLabel } from './integration-state';
import {
  ROUTING_REFUSAL_REASONS,
  type RoutingLeader,
  type RoutingMember,
  type RoutingRefusalReason,
  type RoutingSaveBody,
  type RoutingSaveErrorCode,
  type RoutingStrategy,
  type RoutingView,
} from './types';

/**
 * §14.5 — the "Orden de uso" editor as PURE functions over the server view: the
 * draft the operator edits, whether it differs from what is in force, the exact
 * save body, and the "hoy se usa" summary. The panel only renders these.
 */

/** Who may change an "Order of use" — the permission B2 gates `PUT routing` with (403 otherwise). */
export const ROUTING_PERMISSION: PermissionKey = 'providers.toggle';

/** A refused or failed routing save, with the BFF's code and — for a 400 — the domain's reason. */
export class RoutingSaveError extends Error {
  constructor(
    readonly code: RoutingSaveErrorCode,
    readonly status: number,
    readonly reason?: RoutingRefusalReason,
  ) {
    super(`routing save refused: ${code}${reason ? ` (${reason})` : ''} (HTTP ${status})`);
    this.name = 'RoutingSaveError';
  }
}

/** A refusal reason the console has copy for, or `undefined`. */
export function refusalReason(raw: unknown): RoutingRefusalReason | undefined {
  return (ROUTING_REFUSAL_REASONS as readonly unknown[]).includes(raw) ? (raw as RoutingRefusalReason) : undefined;
}

/** Stable id of a routed key — `transport-dispatch`. */
export function routingKey(view: Pick<RoutingView, 'category' | 'capability'>): string {
  return `${view.category}-${view.capability}`;
}

/** One draft member: position is the array index. */
export interface RoutingDraftMember {
  vendor: string;
  enabled: boolean;
  weight?: number;
}

export interface RoutingDraft {
  strategy: RoutingStrategy;
  members: RoutingDraftMember[];
}

/** The draft a view opens with: its strategy, its members in priority order. */
export function draftFromView(view: RoutingView): RoutingDraft {
  return {
    strategy: view.strategy,
    members: [...view.members]
      .sort((a, b) => a.priority - b.priority)
      .map((m) => ({ vendor: m.vendor, enabled: m.enabled, ...(m.weight !== undefined ? { weight: m.weight } : {}) })),
  };
}

/** Does the draft differ from the view it was opened on? */
export function isDirty(draft: RoutingDraft, view: RoutingView): boolean {
  const base = draftFromView(view);
  if (draft.strategy !== base.strategy || draft.members.length !== base.members.length) return true;
  return draft.members.some(
    (m, i) => m.vendor !== base.members[i]?.vendor || m.enabled !== base.members[i]?.enabled,
  );
}

/** Move the member at `index` by `delta` (−1 up, +1 down); out of range is a no-op. */
export function moveMember(draft: RoutingDraft, index: number, delta: -1 | 1): RoutingDraft {
  const target = index + delta;
  if (index < 0 || index >= draft.members.length || target < 0 || target >= draft.members.length) return draft;
  const members = [...draft.members];
  const [moved] = members.splice(index, 1);
  members.splice(target, 0, moved as RoutingDraftMember);
  return { ...draft, members };
}

/** Park / unpark one member. */
export function setParticipation(draft: RoutingDraft, vendor: string, enabled: boolean): RoutingDraft {
  return { ...draft, members: draft.members.map((m) => (m.vendor === vendor ? { ...m, enabled } : m)) };
}

/**
 * The exact `PUT` body: priorities renumbered 1..n in on-screen order, weights
 * only under `ROUND_ROBIN` (anything else refuses them), `expectedVersion` from
 * the view the draft was opened on (0 = nothing stored yet), reason when given.
 */
export function toSaveBody(draft: RoutingDraft, base: Pick<RoutingView, 'version'>, reason: string): RoutingSaveBody {
  const trimmed = reason.replace(/\s+/g, ' ').trim();
  return {
    strategy: draft.strategy,
    members: draft.members.map((m, i) => ({
      vendor: m.vendor,
      priority: i + 1,
      ...(draft.strategy === 'ROUND_ROBIN' && m.weight !== undefined ? { weight: m.weight } : {}),
      enabled: m.enabled,
    })),
    expectedVersion: base.version ?? 0,
    ...(trimmed ? { reason: trimmed } : {}),
  };
}

/** What the routing in force does right now, for the "hoy se usa" line. */
export type InUseSummary =
  | { kind: 'first'; vendor: string }
  | { kind: 'parallel'; vendors: string[] }
  | { kind: 'rotation'; vendors: string[] }
  | { kind: 'eachOwn'; vendors: string[] }
  | { kind: 'none' };

/**
 * Derived from the SAVED view (what the next call does), never the draft:
 * participating + serving members in order; the first of them for an ordered
 * strategy, all of them for `SCATTER_GATHER` ("todos en paralelo") and
 * `ROUND_ROBIN`.
 *
 * `ai` with no saved order (`platform-default`) is `eachOwn`: every AI feature
 * keeps its own configured provider first (integrations-tds §16.2), so there is
 * no one "first" to name — the serving providers are listed instead.
 */
export function inUseSummary(
  view: Pick<RoutingView, 'strategy' | 'members'> & Partial<Pick<RoutingView, 'category' | 'source'>>,
): InUseSummary {
  const live = [...view.members]
    .sort((a, b) => a.priority - b.priority)
    .filter((m: RoutingMember) => m.enabled && m.serving)
    .map((m) => m.vendor);
  if (live.length === 0) return { kind: 'none' };
  if (view.category === 'ai' && view.source === 'platform-default') return { kind: 'eachOwn', vendors: live };
  if (view.strategy === 'SCATTER_GATHER') return { kind: 'parallel', vendors: live };
  if (view.strategy === 'ROUND_ROBIN' && live.length > 1) return { kind: 'rotation', vendors: live };
  return { kind: 'first', vendor: live[0] as string };
}

/* ─────────────── The airline always wins (integrations-tds §14.1 leader) ─────────────── */

const outranks = (leader: RoutingLeader, vendor: string): boolean =>
  vendor !== leader.vendor && (leader.over === '*' || leader.over.includes(vendor));

/**
 * Does the draft keep the leader rule? The same rule the server refuses a save by: the leader
 * comes before every member it outranks (parked or not) and takes part whenever one of them does —
 * always, under `'*'`. No leader (or not a member) → nothing to keep.
 */
export function keepsLeader(draft: RoutingDraft, leader: RoutingLeader | undefined): boolean {
  if (!leader) return true;
  const at = draft.members.findIndex((m) => m.vendor === leader.vendor);
  if (at < 0) return true;
  const others = draft.members.filter((m) => outranks(leader, m.vendor));
  if (draft.members.slice(0, at).some((m) => outranks(leader, m.vendor))) return false;
  const mustTakePart = leader.over === '*' || others.some((m) => m.enabled);
  return !mustTakePart || draft.members[at]?.enabled === true;
}

/** The leader is locked FIRST (`'*'`): it cannot move at all, nor be parked. */
export function isLockedFirst(leader: RoutingLeader | undefined, vendor: string): boolean {
  return leader?.over === '*' && leader.vendor === vendor;
}

/** May `vendor` move by `delta` without breaking the rule (and within bounds)? */
export function canMove(draft: RoutingDraft, leader: RoutingLeader | undefined, vendor: string, delta: -1 | 1): boolean {
  const index = draft.members.findIndex((m) => m.vendor === vendor);
  const next = moveMember(draft, index, delta);
  return next !== draft && keepsLeader(next, leader);
}

/** May `vendor` be parked / unparked without breaking the rule? */
export function canSetParticipation(
  draft: RoutingDraft,
  leader: RoutingLeader | undefined,
  vendor: string,
  enabled: boolean,
): boolean {
  return keepsLeader(setParticipation(draft, vendor, enabled), leader);
}

/** «Fija en el primer lugar: …» — the why of the leader's fixed place, for a hover or a line. */
export function leaderWhy(t: TFunction<'network'>, view: Pick<RoutingView, 'category'>, leader: RoutingLeader): string {
  if (leader.over === '*') return t('providers.routing.leader.first');
  return t('providers.routing.leader.before', {
    names: leader.over.map((vendor) => vendorLabel(t, view.category, vendor)).join(' · '),
  });
}
